import { createApi } from "@reduxjs/toolkit/query/react";
// import { REHYDRATE } from "redux-persist";
import { convertToFormData } from "@/helpers/data_helper";
import { getCurrentUser } from "@/helpers/session_helper";
import axios from "axios";
import { isEmpty } from "lodash";
import { publicRoutes } from "@/routes/AllRoutes";

function createAbortSignalWithTimeout(timeout) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Ensure the timeout is cleared if the request completes
  controller.signal.addEventListener("abort", () => clearTimeout(timeoutId));
  return controller.signal;
}

const apiKeys = {
  lms: process.env.EXPO_PUBLIC_LMS_API_KEY,
  ssov2: process.env.EXPO_PUBLIC_SSOV2_API_KEY,
};

axios.interceptors.request.use(
  (config) => {
    const publicPaths = publicRoutes.map(route => route.path);
    
    if (publicPaths.includes(window.location.pathname)) {
      return config;
    }

    const tokenExpiryDate = getCurrentUser()?.tokenExpiryDate;

    if (!tokenExpiryDate) {
      window.location.href = '/logout';
      return Promise.reject(new Error('No token expiry date'));
    }

    if (new Date(tokenExpiryDate) <= new Date()) {
      window.location.href = '/logout';
      return Promise.reject(new Error('Token expired'));
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const axiosBaseQuery =
  ({ baseUrl, timeout = 60000 }) =>
    // eslint-disable-next-line implicit-arrow-linebreak
    async ({ url, method, data, params, headers, ...rest }, api) => {
      try {
        const signal = createAbortSignalWithTimeout(timeout); // Use timeout signal

        // Check if data has files to convert to FormData and make the content type 'multipart/form-data'
        const dataHasFiles = Object.keys(data).some(
          (key) =>
            ["file", "photo"].some((keyword) =>
              key.toLowerCase().includes(keyword)
            ) &&
            !key.toLowerCase().includes("fileid")
        );

        const finalData = dataHasFiles ? convertToFormData(data) : data;

        const contentType = dataHasFiles
          ? "multipart/form-data"
          : "application/json";
        const tokenLocation = api.getState()?.api?.mutations?.currentUser;
        const token = tokenLocation?.data?.returnData?.token || "";

        const filteredUrl = url.split("/"); // split URL to get the root path
        const keyToUse = apiKeys[filteredUrl[3]];

        const result = await axios({
          url,
          baseURL: baseUrl,
          method,
          data: finalData,
          params,
          signal, // Attach signal for cancellation
          headers: {
            accept: "application/json, text/plain, */*",
            ...(keyToUse && { "x-api-key": keyToUse }),
            ...(!isEmpty(token) && { Authorization: `Bearer ${token}` }),
            "Content-Type": contentType,
            ...headers,
          },
          ...rest,
        });

        return { data: result };
      } catch (axiosError) {
        const err = axiosError;
        let errorData = {
          status: err.response?.status,
          data: err.response?.data || err.message,
        };
        if (err.code === "ECONNABORTED" || err.message === "Aborted") {
          errorData = {
            status: 408,
            data: "The request took too long to complete.",
          };
        }

        return { error: errorData };
      }
    };

const baseApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: "",
    timeout: 60000, // Optional: Set a default timeout
  }),
  // extractRehydrationInfo(action, { reducerPath }) {
  //   if (action.type === REHYDRATE && action?.payload) {
  //     if (action?.key === "root") {
  //       return action?.payload[reducerPath];
  //     }
  //     return action?.payload;
  //   }
  // },
  refetchOnReconnect: true,
  endpoints: () => ({}),
});

export { baseApi };
